import {
  PatientEncounter,
} from '../../../../patient/components/models/patient-encounter';
import {GuestToken} from '../../../../security/services/SSO';
import {
  ApplicationStatusState,
  GetError,
} from '../../../../security/model/application-status';
import React, {useState} from 'react';
import HttpInterceptor from '../../../../shared/Http/http-interceptor';
import {patientService} from '../../../../patient/services/patient-service';
import {
  specialistClientService,
} from '../../../../implementation-specialist/services/implementation-client-service';
import {
  AuthorizationDocumentStatus,
} from '../payment-program-enrollment/payment-program-enrollment-page';
import {getDynamicContent} from '../no-sig-dynamic-content';
import {ApprovalView} from '../approval-view';
import {getMethodApprovedFromSearch} from '../get-method-approved';

interface PhiConsentApprovalViewProps {
  patientEncounter: PatientEncounter;
  selectedAuthDocStatus: AuthorizationDocumentStatus | null;
  guestToken: GuestToken | null;
  setMessage: (msg: ApplicationStatusState) => void;
  authDocPdfBlob: Blob | null;
}

export const PhiConsentApprovalView = (props: PhiConsentApprovalViewProps) => {

  const {
    patientEncounter,
    guestToken,
    selectedAuthDocStatus,
    setMessage,
    authDocPdfBlob,
  } = props;

  const [modalOpen, setModalOpen] = useState(false);
  const [isThankYou, setIsThankYou] = useState(false);
  const [clientDetails, setClientDetails] = useState({
    surveyUrl: '',
    clientName: '',
    email: '',
    phoneNumber: '',
  });

  const handleApproveBtnOnClick = async () => {

    HttpInterceptor(false, guestToken!, true);

    const resolvedPromises = await Promise.all([
      processAuthDocApproval({
        authorizationDocumentStatusId: selectedAuthDocStatus?.authorizationDocumentStatusId!,
      }),
      specialistClientService.getClient(patientEncounter.clientId),
    ]);

    for (let i = 0; i < resolvedPromises.length; i++) {
      if (resolvedPromises?.[i]?.hasErrors) {
        setMessage(GetError(
            'A problem occurred while approving your PHI consent. Please reach out to a FinPay administrator.'));
        return;
      }
    }

    const fetchedClientDetail = resolvedPromises[1]?.entity?.fullClient;

    setIsThankYou(true);
    setClientDetails({
      ...clientDetails,
      clientName: fetchedClientDetail?.clientName,
      email: fetchedClientDetail?.primaryContact?.email,
      phoneNumber: fetchedClientDetail?.primaryContact?.primPhoneNum,
    });

  };

  const dynamicContent = getDynamicContent({
    typeOfDoc: selectedAuthDocStatus?.authorizationDocuments?.typeOfDoc!,
    patientEncounter,
    modalState: {
      state: modalOpen,
      setState: setModalOpen,
    },
    pdfBlob: authDocPdfBlob,
  });

  return (
      <ApprovalView
          pageContent={{
            title: `PHI Consent Summary`,
            body: {
              intro: 'Information may be shared with',
              details: dynamicContent?.phiDetails!,
              explanation: '',
              instructions: 'for applicable Terms & Conditions.',
              checkboxLabel: `I authorize ${patientEncounter.client.clientName} at ${patientEncounter.facilityName} to share protected health information (PHI) with the individuals listed above.`,
            },
            footerNotes: [],
          }}
          docModal={dynamicContent.docModal}
          displayThankYouView={isThankYou}
          clientDetails={clientDetails}
          modalOpenState={[modalOpen, setModalOpen]}
          setMessage={setMessage}
          handleApprovedBtnOnClick={handleApproveBtnOnClick}
          thankYouViewConfig={{
            thankYouText: 'Thank you for completing your PHI consent.',
            enableSurvey: false,
          }}
      />
  );

};

const processAuthDocApproval = async (params: {
  authorizationDocumentStatusId: number
}) => {

  const {authorizationDocumentStatusId} = params;
  const methodApproved = getMethodApprovedFromSearch();

  const authDocResponse = await patientService.updateAuthorizationDocumentStatus(
      {
        dataToSave: {
          documentStatus: 'signed',
          dateApproved: new Date().toISOString(),
          ...methodApproved && {methodApproved},
        },
        authorizationDocumentStatusId: authorizationDocumentStatusId,
      });

  if (authDocResponse?.hasErrors) {
    return authDocResponse;
  }

  const docCreationResponse = await patientService.createNoSigHelloSignDoc({
    authorizationDocumentStatusId,
  });

  if (docCreationResponse?.hasErrors) {

    await patientService.updateAuthorizationDocumentStatus(
        {
          dataToSave: {
            documentStatus: 'sent',   //reverting status back to sent to avoid conflicts
            dateApproved: null,
          },
          authorizationDocumentStatusId: authorizationDocumentStatusId,
        });

    return docCreationResponse;
  }

};
